// Core packages
import {
  HttpClient, HttpErrorResponse, HttpHeaders
} from "@angular/common/http";
import { Injectable } from "@angular/core";

// Third party packages
import moment from "moment";
import { EMPTY, Observable, of, throwError } from "rxjs";
import { catchError } from "rxjs/operators";

// Custom packages
import { ConfigService } from "./config.service";

@Injectable({
  providedIn: "root"
})
export class OpenapiService {
  private httpOptions: any;

  /**
   * Class constructor
   */
  constructor(
    private configService: ConfigService,
    private http: HttpClient,
  ) {
    this.httpOptions = {};
  }

  /**
   * Get an existing valid token or create one
   * 
   * @returns string The valid API token
   */
  public getValidToken(): Observable<string> {
    try {
      return new Observable<string>(obs => {
        const basicAuth = btoa(`${this.configService.openApiUsername}:${this.configService.openAPiPassword}`);
        let token = '';

        // Get the list of valid OpenApi tokens
        const headers = new HttpHeaders().set('Authorization', `Basic ${basicAuth}`);
        const options = this.httpOptions;
        options.headers = headers;
        this.http
          .get('https://oauth.openapi.it/token', options)
          .pipe(catchError(this.handleError))
          .subscribe(
            (res) => {
              if (
                res &&
                res.success &&
                res.data &&
                Array.isArray(res.data)
              ) {
                // The token exists and verifies that at least one is valid
                // Get tokens with valid scopes
                const validTokens = res.data.filter((el: any) =>
                  el.scopes &&
                  Array.isArray(el.scopes) &&
                  el.scopes.some((scope: string) =>
                    scope.toLowerCase().includes('imprese') || scope.toLowerCase().includes('visurecamerali'))
                );

                // Get the first token valid for at least 24 hours
                const validToken = validTokens.find((el: any) =>
                  moment.unix(el.expire).subtract(1, 'day').diff(moment(), 'day') > 0
                );
                if (validToken && validToken.token) {
                  token = validToken.token;
                }
              }

              if (!token || token === '') {
                // A valid token does not already exist, so create one
                const body = {
                  "scopes": [
                    "*:*.openapi.it/*",
                    "*:*.openapi.com/*"
                  ],
                };
                this.http
                  .post('https://oauth.openapi.it/token', body, options)
                  .pipe(catchError(this.handleError))
                  .subscribe(
                    (res) => {
                      if (
                        res &&
                        res.success &&
                        res.data
                      ) {
                        token = res.data.token;
                      }
                      obs.next(token);
                    },
                    (error) => {
                      console.warn(error);
                      return obs.error(error);
                    },
                    () => {
                      return obs.complete();
                    }
                  );
              } else {
                obs.next(token);
              }
            },
            (error) => {
              console.warn(error);
              return obs.error(error);
            },
            () => {
              return obs.complete();
            }
          );
      });
    } catch (error) {
      console.warn(error);
      return EMPTY;
    }
  }

   /**
   * Get an existing company by Vat number
   * 
   * @param token OpenAPI token
   * @param vatNumber The company Vat number 
   * 
   * @returns object|null The company data found or null
   */
   public getCompanyByVatNumber(token: string, vatNumber: string): Observable<any> {
    try {
      return new Observable<any>(obs => {
        const bearerToken = `Bearer ${token}`;
        const headers = new HttpHeaders().set('Authorization', `Basic ${bearerToken}`);
        const options = this.httpOptions;
        options.headers = headers;

        let italyVataNumber = '';
        if (vatNumber && vatNumber.substring(0, 2).toUpperCase() === 'IT') {
          italyVataNumber = vatNumber.slice(2);
        } else {
          italyVataNumber = vatNumber;
        }
        const url = `https://company.openapi.com/IT-advanced/${italyVataNumber}`;
        this.http
          .get(url, options)
          .pipe(catchError(this.handleError))
          .subscribe(
            (res) => {
              if (
                res &&
                res.success &&
                res.data
              ) {
                obs.next(res.data);
              } else {
                obs.next();
              }
            },
            (error) => {
              console.warn(error);
              return obs.error(error);
            },
            () => {
              return obs.complete();
            }
          );
      });
    } catch (error) {
      console.warn(error);
      return EMPTY;
    }
  }

   /**
   * Get an existing company by Name
   * 
   * @param token OpenAPI token
   * @param companyName The company name 
   * 
   * @returns object|null The company data found or null
   */
   public getCompanyByCompanyName(token: string, companyName: string): Observable<any> {
    try {
      return new Observable<any>(obs => {
        const bearerToken = `Bearer ${token}`;
        const headers = new HttpHeaders().set('Authorization', `Basic ${bearerToken}`);
        const options = this.httpOptions;
        options.headers = headers;

        const url = `https://company.openapi.com/IT-search?dataEnrichment=address&radius=100000&province=MI&limit=10&companyName=${companyName}`;

        this.http
          .get(url, options)
          .pipe(catchError(this.handleError))
          .subscribe(
            (res) => {
              if (
                res &&
                res.success &&
                res.data
              ) {
                obs.next(res.data);
              } else {
                obs.next();
              }
            },
            (error) => {
              console.warn(error);
              return obs.error(error);
            },
            () => {
              return obs.complete();
            }
          );
      });
    } catch (error) {
      console.warn(error);
      return EMPTY;
    }
  }

  
  /**
   * Handle http errors
   *
   * @since 1.0.0
   */
  private handleError(error: HttpErrorResponse): Observable<any> {
    // const message = error.error.message;
    // if (typeof context.toastrService !== 'undefined') {
    //   context.toastrService.error(message);
    // }
    if (error.status === 0) {
      return of(
        new Error("Unexpected error. Please reload the page and try again")
      );
    }
    return throwError(error);
  }

}  